import type { User } from '@app/shared/models/user.model';
import { refreshTokenService } from '@app/shared/services/login.service';
import { useAuthStore } from '@app/shared/store/auth.store';

/**
 * Cross-tab single-flight token-refresh coordinator.
 *
 * Problem: every tab of an origin shares `localStorage['auth']` and runs its
 * own refresh machinery (proactive timer + 401 interceptor). The backend
 * ROTATES the refresh token and REVOKES-ON-REUSE, so two tabs refreshing with
 * the same stored token → one wins, the other 401s → `logout()` flips
 * `isAuthenticated` true↔false (and can revoke every session).
 *
 * Fix: serialize the refresh across tabs with the Web Locks API so AT MOST ONE
 * `/auth/refresh` is issued for a given refresh-token generation. A tab that
 * finds the credentials already rotated (another tab won the lock) ADOPTS them
 * instead of refreshing with the now-stale token. Genuine failure still
 * surfaces so callers preserve the existing logout / impersonation-restore.
 *
 * `navigator.locks` is authoritative. When unavailable (old browser) we degrade
 * to today's per-tab best-effort (run directly) — NOT a fabricated lock.
 * Propagation of a completed rotation to other tabs is a SIGNAL ONLY on
 * `BroadcastChannel('auth')` (`{type:'auth-rotated', gen}`); receivers re-read
 * the canonical auth state — the access token is never put on the channel.
 */

export type RefreshOutcome = 'rotated' | 'adopted' | 'failed';

export const AUTH_BROADCAST_CHANNEL = 'auth';
const LOCK_NAME = 'auth-token-refresh';
// Safety net for a genuinely HUNG refresh so the lock can't be held forever.
// Set well above the platform cold-pooler / container-swap budget (~7.5s) so a
// slow-but-valid `/auth/refresh` is NEVER timed out into a spurious logout —
// the backend rotates + revokes-on-reuse, so killing a slow success would
// consume the refresh token and strand the session.
const REFRESH_TIMEOUT_MS = 30_000;

export type AuthRotationMessage = { type: 'auth-rotated' };

/** Minimal shape of the persisted zustand `auth` blob we read canonically. */
type CanonicalAuth = {
  token: string;
  refreshToken: string;
  user: User;
};

// ── Lock seam (injectable so jsdom unit tests — which lack navigator.locks —
//    can drive deterministic concurrency) ───────────────────────────────────
export type LockRunner = <T>(name: string, fn: () => Promise<T>) => Promise<T>;

let lockRunnerOverride: LockRunner | null = null;

/** Test-only: inject a deterministic lock runner. Pass null to restore. */
export function __setLockRunnerForTests(runner: LockRunner | null): void {
  lockRunnerOverride = runner;
}

function webLocksAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof (navigator as Navigator & { locks?: unknown }).locks !==
      'undefined' &&
    typeof (navigator as Navigator & { locks?: { request?: unknown } }).locks
      ?.request === 'function'
  );
}

function runWithLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  if (lockRunnerOverride) return lockRunnerOverride(name, fn);
  if (webLocksAvailable()) {
    // The lock auto-releases when `fn` settles AND on tab/context destruction,
    // so a crashed holder never starves the origin.
    return navigator.locks.request(name, { mode: 'exclusive' }, async () =>
      fn()
    );
  }
  // Best-effort degrade: no cross-tab mutual exclusion available. Run directly;
  // intra-tab single-flight (the timer's single handle + interceptor's
  // isRefreshing guard) still applies. Documented limitation — NOT a real lock.
  return fn();
}

// ── Broadcast propagation (signal only — never the token) ───────────────────
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
  return channel;
}

function broadcastRotation(): void {
  const ch = getChannel();
  if (!ch) return; // storage-event fallback handles propagation
  const msg: AuthRotationMessage = { type: 'auth-rotated' };
  try {
    ch.postMessage(msg);
  } catch {
    // ignore — storage-event fallback still propagates
  }
}

/**
 * Subscribe to cross-tab rotation signals. The handler is expected to adopt the
 * canonical session (see `adoptRotatedSession`). Returns an unsubscribe fn.
 * Wired by the API layer's multi-tab sync in Phase 2.
 */
export function subscribeToAuthRotations(handler: () => void): () => void {
  const ch = getChannel();
  if (!ch) return () => undefined;
  const listener = (ev: MessageEvent<AuthRotationMessage>) => {
    if (ev?.data?.type === 'auth-rotated') handler();
  };
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}

// ── Canonical auth read ─────────────────────────────────────────────────────
/**
 * Read the canonical persisted auth credentials straight from
 * `localStorage['auth']` (the rotating tab wrote them there via `setSession`).
 * A subscriber tab's in-memory store is NOT auto-updated by another tab's
 * write, so adoption must read the canonical blob.
 */
export function readCanonicalAuth(): CanonicalAuth | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem('auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      state?: { token?: string; refreshToken?: string; user?: User };
    };
    const token = parsed?.state?.token;
    const refreshToken = parsed?.state?.refreshToken;
    const user = parsed?.state?.user;
    if (!token || !refreshToken || !user) return null;
    return { token, refreshToken, user };
  } catch {
    return null;
  }
}

/**
 * THE one canonical adoption path. Used by the coordinator, the proactive
 * timer, the 401 interceptor, the BroadcastChannel handler, AND the storage
 * listener. Re-reads canonical credentials, adopts them into the in-memory
 * store (only if they differ — never a partial `setToken`), and re-arms the
 * proactive refresh ONCE. Never issues a refresh. Never logs out on a token
 * CHANGE (only an explicit clear logs out, handled elsewhere).
 *
 * `user` is adopted alongside the tokens on purpose: tabs of one origin share a
 * single session, so the canonical `user` is authoritative. Copying it keeps
 * the store from ever holding a rotated token beside a stale user (the exact
 * "impersonated token + sadmin user" mismatch the refresh paths guard against).
 * Impersonation is origin-global (the impersonation store is also shared
 * localStorage), so an impersonating session's banner shows in every tab —
 * adoption does not create a hidden cross-identity session.
 */
export async function adoptRotatedSession(): Promise<void> {
  const canonical = readCanonicalAuth();
  if (!canonical) return;
  const current = useAuthStore.getState();
  if (
    canonical.token !== current.token ||
    canonical.refreshToken !== current.refreshToken
  ) {
    current.setSession({
      token: canonical.token,
      refreshToken: canonical.refreshToken,
      user: canonical.user,
    });
  }
  const { scheduleTokenRefresh } = await import('./token-refresh-timer');
  scheduleTokenRefresh(canonical.token);
}

// ── Timeout helper ──────────────────────────────────────────────────────────
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error('auth-refresh-timeout')), ms);
    p.then(
      (v) => {
        clearTimeout(id);
        resolve(v);
      },
      (e) => {
        clearTimeout(id);
        reject(e);
      }
    );
  });
}

/**
 * Perform a cross-tab-coordinated token refresh.
 *
 * - `'rotated'`  — this tab performed the single `/auth/refresh` and committed
 *                  the new session.
 * - `'adopted'`  — another tab already rotated; this tab adopted the new
 *                  credentials without a network call (caller must NOT log out).
 * - `'failed'`   — a genuine failure with no concurrent rotation; the caller
 *                  runs its existing logout / impersonation-restore path.
 *
 * Offline sessions are gated by the CALLER (the proactive timer skips an
 * offline session entirely). The 401 interceptor never reaches here offline —
 * a 401 means the request did reach the server, i.e. we are online.
 */
export async function coordinatedRefresh(): Promise<RefreshOutcome> {
  const s0 = useAuthStore.getState();
  const accessBefore = s0.token;
  if (!s0.refreshToken) return 'failed';

  return runWithLock(LOCK_NAME, async () => {
    // Re-read canonical AFTER acquiring the lock — another tab may have rotated
    // while we waited. Access-token string inequality is the rotation signal.
    const canonical = readCanonicalAuth();
    if (canonical && canonical.token !== accessBefore) {
      await adoptRotatedSession();
      return 'adopted';
    }

    const currentRefresh =
      useAuthStore.getState().refreshToken || s0.refreshToken;
    try {
      const resp = await withTimeout(
        refreshTokenService(currentRefresh),
        REFRESH_TIMEOUT_MS
      );
      const after = useAuthStore.getState();
      after.setSession({
        token: resp.accessToken,
        refreshToken: resp.refreshToken,
        user: (resp.user as User | undefined) ?? after.user,
      });
      const { scheduleTokenRefresh } = await import('./token-refresh-timer');
      scheduleTokenRefresh(resp.accessToken);
      broadcastRotation();
      return 'rotated';
    } catch {
      // Defense-in-depth: if the canonical token changed since we started,
      // another tab rotated during our (failed/slow) attempt — adopt, do NOT
      // log out. Otherwise it is a genuine failure.
      const afterFail = readCanonicalAuth();
      if (afterFail && afterFail.token !== accessBefore) {
        await adoptRotatedSession();
        return 'adopted';
      }
      return 'failed';
    }
  });
}
